import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'vidhub_token';

export const api = axios.create({ baseURL: API_BASE });

api.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// Global 413 (quota-exceeded) handler — polished toast with upgrade CTA
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 413) {
      const d = err.response.data?.detail;
      if (d && typeof d === 'object' && d.error === 'quota_exceeded') {
        const usedGb = (d.used_mb / 1024).toFixed(2);
        const quotaGb = d.quota_mb ? (d.quota_mb / 1024).toFixed(1) : '—';
        toast.error(`Storage full — ${usedGb} GB / ${quotaGb} GB`, {
          description: 'Upgrade your plan or free up space to continue.',
          action: {
            label: 'Upgrade',
            onClick: () => { window.location.href = '/memberships'; },
          },
          duration: 7000,
        });
      } else if (d && typeof d === 'object' && d.error === 'file_too_large') {
        toast.error('File too large', { description: d.message, duration: 5000 });
      }
    }
    return Promise.reject(err);
  }
);

export const setToken = (t) => {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);

// ---- Auth ----
export const signupApi = (email, password, name, extras = {}) => api.post('/auth/signup', { email, password, name, ...extras }).then(r => r.data);
export const loginApi = (email, password) => api.post('/auth/login', { email, password }).then(r => r.data);
export const meApi = () => api.get('/auth/me').then(r => r.data);

// ---- Channels ----
export const listChannels = () => api.get('/channels').then(r => r.data);
export const getChannel = (id) => api.get(`/channels/${id}`).then(r => r.data);
export const updateChannel = (id, patch) => api.put(`/channels/${id}`, patch).then(r => r.data);
export const toggleSubscribe = (id) => api.post(`/channels/${id}/subscribe`).then(r => r.data);

// ---- Videos ----
export const listVideos = (params = {}) => api.get('/videos', { params }).then(r => r.data);
export const getVideo = (id) => api.get(`/videos/${id}`).then(r => r.data);
export const createVideo = (payload) => api.post('/videos', payload).then(r => r.data);
export const deleteVideo = (id) => api.delete(`/videos/${id}`).then(r => r.data);
export const recordView = (id) => api.post(`/videos/${id}/view`).then(r => r.data);
export const toggleLike = (id) => api.post(`/videos/${id}/like`).then(r => r.data);
export const toggleWatchLater = (id) => api.post(`/videos/${id}/watchlater`).then(r => r.data);

export const uploadFile = (file, onProgress) => {
  const fd = new FormData();
  fd.append('file', file);
  return api.post('/videos/upload', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  }).then(r => r.data);
};

// ---- Comments ----
export const listComments = (vid) => api.get(`/videos/${vid}/comments`).then(r => r.data);
export const addComment = (vid, text) => api.post(`/videos/${vid}/comments`, { text }).then(r => r.data);

// ---- Me / Library ----
export const myHistory = () => api.get('/me/history').then(r => r.data);
export const myLiked = () => api.get('/me/liked').then(r => r.data);
export const myWatchLater = () => api.get('/me/watchlater').then(r => r.data);
export const mySubscriptions = () => api.get('/me/subscriptions').then(r => r.data);
export const setPlan = (plan) => api.put('/me/plan', { plan }).then(r => r.data);

// ---- Admin ----
export const adminStats = () => api.get('/admin/stats').then(r => r.data);
export const updateAnnouncement = (message) => api.put('/admin/announcement', { message }).then(r => r.data);
export const getAnnouncement = () => api.get('/announcement').then(r => r.data);

// ---- Admin operational backend ----
export const adminListUsers = (params = {}) => api.get('/admin/users', { params }).then(r => r.data);
export const adminToggleBan = (uid, banned) => api.put(`/admin/users/${uid}/ban`, { banned }).then(r => r.data);
export const adminCleanupTestUsers = () => api.post('/admin/users/test-cleanup').then(r => r.data);
export const adminModerationRecent = (limit = 30) => api.get('/admin/moderation/recent', { params: { limit } }).then(r => r.data);
export const adminDeleteTrack = (tid) => api.delete(`/admin/content/track/${tid}`).then(r => r.data);
export const adminDeleteHub = (hid) => api.delete(`/admin/content/hub/${hid}`).then(r => r.data);
export const adminAnalytics = (days = 30) => api.get('/admin/analytics/overview', { params: { days } }).then(r => r.data);
export const adminAudit = (params = {}) => api.get('/admin/audit', { params }).then(r => r.data);
export const adminToursCsvUrl = () => `${API_BASE}/admin/tour-leads.csv`;
export const adminResetPassword = (uid, new_password) => api.put(`/admin/users/${uid}/reset-password`, { new_password }).then(r => r.data);

// ---- Account: password ----
export const changePassword = async (body) => {
  const r = await api.put('/me/password', body);
  // Server rotates token_version on password change; adopt the fresh token so the current session continues.
  if (r.data?.token) setToken(r.data.token);
  return r.data;
};
export const logoutAll = async () => {
  const r = await api.post('/auth/logout-all');
  if (r.data?.token) setToken(r.data.token);
  return r.data;
};
export const requestPasswordReset = (email) => api.post('/auth/request-password-reset', { email }).then(r => r.data);
export const performPasswordReset = (body) => api.post('/auth/perform-password-reset', body).then(r => r.data);

// ---- Categories / Settings ----
export const listCategories = () => api.get('/categories').then(r => r.data);
export const createCategory = (name, slug) => api.post('/categories', { name, slug }).then(r => r.data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`).then(r => r.data);
export const getPublicSettings = () => api.get('/settings').then(r => r.data);

// ---- Owner ----
export const ownerStats = () => api.get('/owner/stats').then(r => r.data);
export const ownerUpdateSettings = (patch) => api.put('/owner/settings', patch).then(r => r.data);
export const ownerListUsers = () => api.get('/owner/users').then(r => r.data);
export const ownerSetRole = (id, role) => api.put(`/owner/users/${id}/role`, { role }).then(r => r.data);
export const ownerDeleteUser = (id) => api.delete(`/owner/users/${id}`).then(r => r.data);

// ---- Ads ----
export const publicAds = (params = {}) => api.get('/ads', { params }).then(r => r.data);
export const listMyAds = () => api.get('/ads/mine').then(r => r.data);
export const listAllAds = () => api.get('/owner/ads').then(r => r.data);
export const createAd = (payload) => api.post('/ads', payload).then(r => r.data);
export const updateAd = (id, patch) => api.put(`/ads/${id}`, patch).then(r => r.data);
export const deleteAd = (id) => api.delete(`/ads/${id}`).then(r => r.data);
export const adImpression = (id) => api.post(`/ads/${id}/impression`).then(r => r.data).catch(() => null);
export const adClick = (id) => api.post(`/ads/${id}/click`).then(r => r.data).catch(() => null);
export const setChannelSponsor = (id, patch) => api.put(`/channels/${id}/sponsor`, patch).then(r => r.data);
export const setVideoMonetization = (id, monetized) => api.put(`/videos/${id}/monetization`, { monetized }).then(r => r.data);

// ---- Stripe / billing ----
export const listPlans = () => api.get('/pricing/plans').then(r => r.data);
export const listAdPackages = () => api.get('/pricing/ad-packages').then(r => r.data);
export const checkoutPlan = (plan_id) => api.post('/checkout/plan', { plan_id, origin_url: window.location.origin }).then(r => r.data);
export const checkoutAd = (package_id, ad_id) => api.post('/checkout/ad', { package_id, ad_id, origin_url: window.location.origin }).then(r => r.data);
export const checkoutStatus = (session_id) => api.get(`/checkout/status/${session_id}`).then(r => r.data);
export const myPayments = () => api.get('/me/payments').then(r => r.data);

// Owner-only pricing editing
export const ownerUpsertPlan = (id, payload) => api.put(`/owner/pricing/plans/${id}`, payload).then(r => r.data);
export const ownerDeletePlan = (id) => api.delete(`/owner/pricing/plans/${id}`).then(r => r.data);
export const ownerUpsertAdPackage = (id, payload) => api.put(`/owner/pricing/ad-packages/${id}`, payload).then(r => r.data);
export const ownerDeleteAdPackage = (id) => api.delete(`/owner/pricing/ad-packages/${id}`).then(r => r.data);
export const ownerSetUserStorage = (uid, mb) => api.put(`/owner/users/${uid}/storage`, { storage_override_mb: mb }).then(r => r.data);

// Storage
export const myStorage = () => api.get('/me/storage').then(r => r.data);

// Media library
export const listMedia = () => api.get('/media').then(r => r.data);
export const uploadMedia = (file, onProgress) => {
  const form = new FormData(); form.append('file', file);
  return api.post('/media/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: e => onProgress?.(e.total ? (e.loaded / e.total) * 100 : 0),
  }).then(r => r.data);
};
export const deleteMedia = (id) => api.delete(`/media/${id}`).then(r => r.data);

// ---- ReaperCast ----
export const rcTiers = () => api.get('/reapercast/tiers').then(r => r.data);
export const rcDistributors = () => api.get('/reapercast/distributors').then(r => r.data);
export const rcAcademy = () => api.get('/reapercast/academy').then(r => r.data);
export const rcSetTier = (tier) => api.put('/me/reapercast-tier', { tier }).then(r => r.data);
export const rcCreateTrack = (payload) => api.post('/reapercast/tracks', payload).then(r => r.data);
export const rcListTracks = (params = {}) => api.get('/reapercast/tracks', { params }).then(r => r.data);
export const rcUpdateTrack = (id, patch) => api.put(`/reapercast/tracks/${id}`, patch).then(r => r.data);
export const rcDeleteTrack = (id) => api.delete(`/reapercast/tracks/${id}`).then(r => r.data);
export const rcNowPlaying = () => api.get('/reapercast/radio/now-playing').then(r => r.data);

// ---- ReaperCast AI Agent ----
export const rcAgentSessions = () => api.get('/reapercast/agent/sessions').then(r => r.data);
export const rcAgentHistory = (sid) => api.get(`/reapercast/agent/sessions/${sid}/messages`).then(r => r.data);
export const rcAgentSend = (payload) => api.post('/reapercast/agent/chat', payload).then(r => r.data);
export const rcAgentDelete = (sid) => api.delete(`/reapercast/agent/sessions/${sid}`).then(r => r.data);

// ---- Academy ----
export const academyCurriculum = () => api.get('/academy/curriculum').then(r => r.data);
export const academyLesson = (trackId, lessonId) => api.get(`/academy/tracks/${trackId}/lessons/${lessonId}`).then(r => r.data);
export const academyTrackAgencies = (trackId) => api.get(`/academy/tracks/${trackId}/agencies`).then(r => r.data);
export const academyUpdateAgencyAction = (trackId, agencyId, body) => api.put(`/academy/tracks/${trackId}/agencies/${agencyId}`, body).then(r => r.data);

export const channelSpotify = (cid) => api.get(`/channels/${cid}/spotify`).then(r => r.data);

// ---- Royalty Dashboard (Studio-tier) ----
export const royaltyDashboard = () => api.get('/royalty/dashboard').then(r => r.data);
export const royaltyEntries = (source) => api.get('/royalty/entries', { params: source ? { source } : {} }).then(r => r.data);
export const royaltyCreateEntry = (body) => api.post('/royalty/entries', body).then(r => r.data);
export const royaltyDeleteEntry = (eid) => api.delete(`/royalty/entries/${eid}`).then(r => r.data);
export const royaltyStatements = () => api.get('/royalty/statements').then(r => r.data);
export const royaltyDeleteStatement = (sid) => api.delete(`/royalty/statements/${sid}`).then(r => r.data);
export const royaltyUploadStatement = (file, source_hint, period_hint) => {
  const fd = new FormData(); fd.append('file', file);
  if (source_hint) fd.append('source_hint', source_hint);
  if (period_hint) fd.append('period_hint', period_hint);
  return api.post('/royalty/statements/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
};

// ---- Academy Action Centers ----
export const academyActionCenter = (trackId) => api.get(`/academy/tracks/${trackId}/action-center`).then(r => r.data);
export const academyActionToggle = (trackId, itemId, done) => api.put(`/academy/tracks/${trackId}/action-center/${itemId}`, { done }).then(r => r.data);

// ---- Income Discovery Tool ----
export const incomeStreams = () => api.get('/income/streams').then(r => r.data);
export const incomeUpdate = (streamId, body) => api.put(`/income/streams/${streamId}`, body).then(r => r.data);
export const incomeAdvisor = (body) => api.post('/income/advisor', body).then(r => r.data);

// ---- Admin: full user editor + plan stats ----
export const adminUpdateUser = (uid, body) => api.put(`/admin/users/${uid}`, body).then(r => r.data);
export const adminPlanStats = () => api.get('/admin/plans/stats').then(r => r.data);

// ---- Spotify (public artist lookup for Academy agencies) ----
export const spotifyStatus = () => api.get('/spotify/status').then(r => r.data);
export const spotifyArtistSearch = (q, limit = 8) => api.get('/spotify/artist/search', { params: { q, limit } }).then(r => r.data);
export const spotifyArtistDetail = (id) => api.get(`/spotify/artist/${id}`).then(r => r.data);
export const academyComplete = (trackId, lessonId, answers) => api.post(`/academy/tracks/${trackId}/lessons/${lessonId}/complete`, { answers }).then(r => r.data);
export const academyMe = () => api.get('/academy/me').then(r => r.data);
export const academyCertificateUrl = (trackId) => `${API_BASE}/academy/certificates/${trackId}.png`;
export const academyDownloadCertificate = async (trackId) => {
  const resp = await api.get(`/academy/certificates/${trackId}.png`, { responseType: 'blob' });
  return URL.createObjectURL(resp.data);
};

// ---- Specialist agents (unlocked by academy) ----
export const specialistChat = (id, payload) => api.post(`/academy/specialists/${id}/chat`, payload).then(r => r.data);
export const specialistSessions = (id) => api.get(`/academy/specialists/${id}/sessions`).then(r => r.data);
export const specialistHistory = (id, sid) => api.get(`/academy/specialists/${id}/sessions/${sid}/messages`).then(r => r.data);
export const specialistDeleteSession = (id, sid) => api.delete(`/academy/specialists/${id}/sessions/${sid}`).then(r => r.data);

// ---- Public channel music ----
export const getChannelMusic = (cid) => api.get(`/channels/${cid}/music`).then(r => r.data);

// ---- Listener flow: tracks, artists, reel ----
export const toggleTrackLike = (tid) => api.post(`/tracks/${tid}/like`).then(r => r.data);
export const recordTrackPlay = (tid) => api.post(`/tracks/${tid}/play`).then(r => r.data).catch(() => null);
export const myLikedTracks = () => api.get('/me/liked-tracks').then(r => r.data);
export const listArtists = (params = {}) => api.get('/artists', { params }).then(r => r.data);
export const reelFeed = (params = {}) => api.get('/feed/reel', { params }).then(r => r.data);

// ---- Research vault ----
export const researchList = (params = {}) => api.get('/research', { params }).then(r => r.data);
export const researchGet = (id) => api.get(`/research/${id}`).then(r => r.data);
export const researchCreate = (payload) => api.post('/research', payload).then(r => r.data);
export const researchUpdate = (id, patch) => api.put(`/research/${id}`, patch).then(r => r.data);
export const researchDelete = (id) => api.delete(`/research/${id}`).then(r => r.data);

// ---- Profile ----
export const setProfileType = (profile_type) => api.put('/me/profile-type', { profile_type }).then(r => r.data);

// ---- Knowledge Hub ----
export const hubBrowse = (params = {}) => api.get('/hub/browse', { params }).then(r => r.data);
export const hubGet = (id) => api.get(`/hub/${id}`).then(r => r.data);
export const hubCreate = (payload) => api.post('/hub', payload).then(r => r.data);
export const hubUpdate = (id, patch) => api.put(`/hub/${id}`, patch).then(r => r.data);
export const hubDelete = (id) => api.delete(`/hub/${id}`).then(r => r.data);
export const hubMyPublications = () => api.get('/hub/my/publications').then(r => r.data);
export const hubMyPurchases = () => api.get('/hub/my/purchases').then(r => r.data);
export const hubAddEpisode = (id, payload) => api.post(`/hub/${id}/episodes`, payload).then(r => r.data);
export const hubDeleteEpisode = (epId) => api.delete(`/hub/episodes/${epId}`).then(r => r.data);
export const hubPurchase = (id, urls) => api.post(`/hub/${id}/purchase`, urls || {}).then(r => r.data);
export const hubPodcastRssUrl = (id) => `${API_BASE}/hub/podcasts/${id}/rss.xml`;

// ---- Changelog ----
export const changelogList = (params = {}) => api.get('/changelog', { params }).then(r => r.data);
export const changelogCreate = (payload) => api.post('/changelog', payload).then(r => r.data);
export const changelogUpdate = (id, patch) => api.put(`/changelog/${id}`, patch).then(r => r.data);
export const changelogDelete = (id) => api.delete(`/changelog/${id}`).then(r => r.data);

// ---- Onboarding tour leads ----
export const tourLead = (payload) => api.post('/tour/lead', payload).then(r => r.data);
export const tourLeadsList = () => api.get('/tour/leads').then(r => r.data);

// ---- Content sanitizer — strip markdown hashes/symbols from AI output for plain display ----
export const cleanAiText = (text) => {
  if (!text) return '';
  return text
    // strip heading hashes: "### Title" => "Title", but keep mid-line hashes (e.g., "#1")
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    // bold/italic markdown markers — strip the markers, keep the text
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/(^|[^*])\*(?!\s)([^*\n]+?)\*(?!\*)/g, '$1$2')
    .replace(/__([^_\n]+)__/g, '$1')
    // inline code backticks (single) — keep content, drop backticks
    .replace(/`([^`\n]+)`/g, '$1')
    // list bullets: keep the bullet visual but strip leading dashes if they appear as lone-line markers
    .replace(/^\s*[-*]\s{3,}/gm, '• ')
    .replace(/^\s*[-*]\s/gm, '• ')
    // numbered lists: keep as-is
    // horizontal rules
    .replace(/^\s*-{3,}\s*$/gm, '');
};

// ---- Beta Program ----
export const betaFeatures = () => api.get('/beta/features').then(r => r.data);
export const betaSignup = (payload) => api.post('/beta/signup', payload).then(r => r.data);
export const betaMe = () => api.get('/beta/me').then(r => r.data);
export const betaFeedback = (payload) => api.post('/beta/feedback', payload).then(r => r.data);
export const betaAdminSignups = (status) => api.get('/beta/admin/signups', { params: status ? { status } : {} }).then(r => r.data);
export const betaAdminFeedback = () => api.get('/beta/admin/feedback').then(r => r.data);
export const betaAdminUpdateSignup = (id, status) => api.put(`/beta/admin/signups/${id}`, { status }).then(r => r.data);

// ---- YouTube migration ----
export const ytResolve = (channel_url) => api.post('/import/youtube/resolve', { channel_url }).then(r => r.data);
export const ytBulkImport = (videos, category = 'trending') => api.post('/import/youtube/bulk', { videos, rights_confirmed: true, category }).then(r => r.data);

// ---- Video import / download ----
export const resolveImportUrl = (url) => api.post('/import/resolve', { url }).then(r => r.data);
export const getDownloadUrl = (id) => api.get(`/videos/${id}/download-url`).then(r => r.data);

// ---- Legal ----
export const listLegal = () => api.get('/legal').then(r => r.data);
export const getLegal = (slug) => api.get(`/legal/${slug}`).then(r => r.data);
export const updateLegal = (slug, content, title) => api.put(`/owner/legal/${slug}`, { content, title }).then(r => r.data);

// ---- Tracked links ----
export const createTrackedLink = (payload) => api.post('/links', payload).then(r => r.data);
export const listTrackedLinks = () => api.get('/links').then(r => r.data);
export const deleteTrackedLink = (id) => api.delete(`/links/${id}`).then(r => r.data);

// ---- Earnings & payouts ----
export const myEarnings = () => api.get('/me/earnings').then(r => r.data);
export const myPayouts = () => api.get('/me/payouts').then(r => r.data);
export const requestPayout = (payload) => api.post('/me/payouts', payload).then(r => r.data);
export const ownerListPayouts = () => api.get('/owner/payouts').then(r => r.data);
export const ownerUpdatePayout = (id, status, note) => api.put(`/owner/payouts/${id}`, { status, note }).then(r => r.data);

// ---- API Keys (Owner vault) ----
export const ownerListKeys = () => api.get('/owner/apikeys').then(r => r.data);
export const ownerCreateKey = (payload) => api.post('/owner/apikeys', payload).then(r => r.data);
export const ownerUpdateKey = (id, patch) => api.put(`/owner/apikeys/${id}`, patch).then(r => r.data);
export const ownerDeleteKey = (id) => api.delete(`/owner/apikeys/${id}`).then(r => r.data);
export const adminListKeys = () => api.get('/admin/apikeys').then(r => r.data);

// Public short link resolver (used for copy-to-share)
export const shortLinkUrl = (slug) => `${API_BASE}/l/${slug}`;

// Server-side click/impression beacons (include optional video_id for earnings)
export const adClickTracked = (id, video_id) => api.post(`/ads/${id}/click${video_id ? `?video_id=${encodeURIComponent(video_id)}` : ''}`).then(r => r.data).catch(() => null);
export const adImpressionTracked = (id, video_id) => api.post(`/ads/${id}/impression${video_id ? `?video_id=${encodeURIComponent(video_id)}` : ''}`).then(r => r.data).catch(() => null);

// Resolve possibly-relative /api/files/... urls to absolute
export const resolveFileUrl = (u) => {
  if (!u) return u;
  if (u.startsWith('/api/')) return `${BACKEND_URL}${u}`;
  return u;
};
